import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getOfferteById, updateOfferte, createOfferte } from '../../api/offerte';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Label } from '../ui/label';
import { AlertCircle, CheckCircle, Save, ArrowLeft, PlusCircle, Trash2, Calendar, User, Mail, Phone, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Alert, AlertDescription } from '../ui/alert';
import { motion } from 'framer-motion';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, format } from "date-fns";

// Color scheme constants consistent with other components
const COLORS = {
  primary: "#BFFF00", // Neon green
  secondary: "#1E4FCF", // Blue
  accent: "#FF6B00", // Orange
  dark: "#05081A", // Dark blue/black
}

const OfferteForm = () => {
  const navigate = useNavigate();
  const { offerteId } = useParams();
  const isEditMode = Boolean(offerteId);

  const BTW_PERCENTAGE_VAST = 21;

  const [offerteData, setOfferteData] = useState({
    klantNaam: '',
    klantEmail: '',
    klantTelefoon: '',
    offerteDatum: format(new Date(), 'yyyy-MM-dd'),
    vervalDatum: '',
    items: [],
    totaalExclBTW: 0,
    btwBedrag: 0,
    totaalInclBTW: 0,
    status: 'concept',
    extraTekstOfferte: '',
    nummer: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const calculateTotals = useCallback((items) => {
    let excl = 0;
    (items || []).forEach(item => {
      const itemTotaal = (parseFloat(item.prijsPerStuk) || 0) * (parseInt(item.aantal, 10) || 0);
      excl += itemTotaal;
    });
    const btw = (excl * BTW_PERCENTAGE_VAST) / 100;
    setOfferteData(prev => ({
      ...prev,
      totaalExclBTW: excl,
      btwBedrag: btw,
      totaalInclBTW: excl + btw,
    }));
  }, [BTW_PERCENTAGE_VAST]);

  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      getOfferteById(offerteId)
        .then(data => {
          const parsedItems = Array.isArray(data.items) ? data.items : JSON.parse(data.items || '[]');
          setOfferteData({
            klantNaam: data.klantNaam || '',
            klantEmail: data.klantEmail || '',
            klantTelefoon: data.klantTelefoon || '',
            offerteDatum: data.offerteDatum ? format(new Date(data.offerteDatum), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
            vervalDatum: data.vervalDatum ? format(new Date(data.vervalDatum), 'yyyy-MM-dd') : '',
            items: parsedItems,
            status: data.status || 'concept',
            extraTekstOfferte: data.extraTekstOfferte || '',
            nummer: data.nummer || '',
            totaalExclBTW: 0, 
            btwBedrag: 0,
            totaalInclBTW: 0,
          });
          calculateTotals(parsedItems);
          setLoading(false);
        })
        .catch(err => {
          console.error("Error fetching offerte:", err);
          setError('Kon offertegegevens niet laden.');
          setLoading(false);
        });
    } else { // Nieuwe offerte
      const today = new Date();
      const defaultVervalDatum = new Date(today);
      defaultVervalDatum.setDate(today.getDate() + 30);
      setOfferteData(prev => ({
        ...prev,
        klantNaam: '',
        klantEmail: '',
        klantTelefoon: '',
        offerteDatum: format(today, 'yyyy-MM-dd'),
        vervalDatum: format(defaultVervalDatum, 'yyyy-MM-dd'),
        items: [],
        status: 'concept',
        extraTekstOfferte: '',
        nummer: '',
        totaalExclBTW: 0,
        btwBedrag: 0,
        totaalInclBTW: 0,
      }));
      calculateTotals([]);
    }
  }, [offerteId, isEditMode, calculateTotals]);

  useEffect(() => {
    if (offerteData.items) {
        calculateTotals(offerteData.items);
    }
  }, [offerteData.items, calculateTotals]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOfferteData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, name) => {
    setOfferteData(prev => ({
      ...prev,
      [name]: date ? format(date, 'yyyy-MM-dd') : ''
    }));
  };

  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    const newItems = [...(offerteData.items || [])];
    const numValue = (name === 'aantal' || name === 'prijsPerStuk') ? parseFloat(value) : value;
    newItems[index] = { ...newItems[index], [name]: numValue };
    setOfferteData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setOfferteData(prev => ({
      ...prev,
      items: [...(prev.items || []), { naam: '', aantal: 1, prijsPerStuk: 0, beschrijving: '' }]
    }));
  };

  const removeItem = (index) => {
    const newItems = (offerteData.items || []).filter((_, i) => i !== index);
    setOfferteData(prev => ({ ...prev, items: newItems }));
  };
  
  const handleStatusChange = (value) => {
    setOfferteData(prev => ({ ...prev, status: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage('');

    const payload = {
      klantNaam: offerteData.klantNaam,
      klantEmail: offerteData.klantEmail,
      klantTelefoon: offerteData.klantTelefoon,
      offerteDatum: offerteData.offerteDatum,
      vervalDatum: offerteData.vervalDatum,
      items: (offerteData.items || []).map(item => ({
          naam: item.naam,
          aantal: parseInt(item.aantal, 10) || 0,
          prijsPerStuk: parseFloat(item.prijsPerStuk) || 0,
          beschrijving: item.beschrijving || '',
      })),
      totaalExclBTW: offerteData.totaalExclBTW,
      btwBedrag: offerteData.btwBedrag,
      totaalInclBTW: offerteData.totaalInclBTW,
      status: offerteData.status,
      extraTekstOfferte: offerteData.extraTekstOfferte,
      btwPercentage: BTW_PERCENTAGE_VAST,
    };

    try {
      if (isEditMode) {
        await updateOfferte(offerteId, payload);
        setSuccessMessage('Offerte succesvol bijgewerkt!');
      } else {
        await createOfferte(payload); 
        setSuccessMessage('Offerte succesvol aangemaakt!');
      }
      setTimeout(() => navigate('/dashboard/offertes'), 2000);
    } catch (err) {
        const errorMsg = (err.response && err.response.data && err.response.data.message) || err.message || `Kon offerte niet ${isEditMode ? 'bijwerken' : 'aanmaken'}.`;
        setError(errorMsg);
        console.error("Submit error:", err.response || err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode && !offerteData.klantNaam && !offerteData.nummer) { 
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-3 border-gray-100 border-t-[#BFFF00] border-r-[#BFFF00] animate-spin"></div>
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <span className="text-xs font-medium text-gray-400">LADEN</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <div className="flex justify-between items-center">
        <div className="relative">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? `Offerte Bewerken` : 'Nieuwe Offerte'}
          </h1>
          <div className="absolute bottom-0 left-0 w-1/2 h-1 bg-[#BFFF00] -z-10 transform translate-y-1 opacity-60"></div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard/offertes')}
            className="border-gray-300 text-gray-700 hover:bg-gray-50/80 rounded-md"
            disabled={loading}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
          </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-[#BFFF00] hover:bg-[#BFFF00]/90 text-[#05081A] font-medium rounded-md"
            disabled={loading || !!successMessage}
          >
            <Save className="mr-2 h-4 w-4" /> {loading ? 'Opslaan...' : 'Offerte Opslaan'}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="border border-red-200 bg-red-50 text-red-800 rounded-lg">
          <div className="flex gap-2 items-center">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="font-medium">{error}</AlertDescription>
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert className="border border-green-200 bg-green-50 text-green-800 rounded-lg">
          <div className="flex gap-2 items-center">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription className="font-medium">{successMessage}</AlertDescription>
          </div>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <User className="h-4 w-4 text-[#1E4FCF]" /> Klantgegevens
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-500" />
                  <Label htmlFor="klantNaam" className="text-sm font-medium">Naam</Label>
                </div>
                <Input 
                  id="klantNaam" 
                  name="klantNaam" 
                  value={offerteData.klantNaam} 
                  onChange={handleChange} 
                  required 
                  className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <Label htmlFor="klantEmail" className="text-sm font-medium">Email</Label>
                </div>
                <Input 
                  id="klantEmail" 
                  name="klantEmail" 
                  type="email" 
                  value={offerteData.klantEmail} 
                  onChange={handleChange} 
                  className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <Label htmlFor="klantTelefoon" className="text-sm font-medium">Telefoon</Label>
                </div>
                <Input 
                  id="klantTelefoon" 
                  name="klantTelefoon" 
                  value={offerteData.klantTelefoon} 
                  onChange={handleChange} 
                  className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#1E4FCF]" /> Offerte Details
            </CardTitle>
            {isEditMode && offerteData.nummer && (
              <CardDescription>Offertenummer: {offerteData.nummer}</CardDescription>
            )}
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <Label htmlFor="offerteDatum" className="text-sm font-medium">Offertedatum</Label>
                </div>
                <DatePicker
                  selected={offerteData.offerteDatum ? parse(offerteData.offerteDatum, 'yyyy-MM-dd', new Date()) : null}
                  onChange={(date) => handleDateChange(date, 'offerteDatum')}
                  dateFormat="dd/MM/yyyy"
                  className="flex h-10 w-full rounded-md border border-gray-200 bg-background px-3 py-2 text-sm focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                  id="offerteDatum"
                  name="offerteDatum"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <Label htmlFor="vervalDatum" className="text-sm font-medium">Vervaldatum</Label>
                </div>
                <DatePicker
                  selected={offerteData.vervalDatum ? parse(offerteData.vervalDatum, 'yyyy-MM-dd', new Date()) : null}
                  onChange={(date) => handleDateChange(date, 'vervalDatum')}
                  dateFormat="dd/MM/yyyy"
                  className="flex h-10 w-full rounded-md border border-gray-200 bg-background px-3 py-2 text-sm focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                  id="vervalDatum"
                  name="vervalDatum"
                  required
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  <Label htmlFor="status" className="text-sm font-medium">Status</Label>
                </div>
                <Select 
                  name="status" 
                  value={offerteData.status} 
                  onValueChange={handleStatusChange}
                >
                  <SelectTrigger className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10">
                    <SelectValue placeholder="Selecteer status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="concept">Concept</SelectItem>
                    <SelectItem value="verzonden">Verzonden</SelectItem>
                    <SelectItem value="geaccepteerd">Geaccepteerd</SelectItem>
                    <SelectItem value="geweigerd">Geweigerd</SelectItem>
                    <SelectItem value="verlopen">Verlopen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#1E4FCF]" /> Items
            </CardTitle>
            <CardDescription>
              Specificeer hier de diensten en producten voor deze offerte
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="hidden md:grid md:grid-cols-12 gap-4 text-sm font-medium text-gray-700 pb-2">
              <div className="md:col-span-6">Naam/Beschrijving</div>
              <div className="md:col-span-2">Aantal</div>
              <div className="md:col-span-3">Prijs/Stuk</div>
              <div className="md:col-span-1"></div>
            </div>

            {(offerteData.items || []).map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="md:col-span-6 space-y-1">
                  <Label htmlFor={`item-naam-${index}`} className="md:hidden text-xs text-gray-600">Naam/Beschrijving</Label>
                  <Input 
                    id={`item-naam-${index}`}
                    name="naam" 
                    placeholder="Naam/Beschrijving" 
                    value={item.naam || ''} 
                    onChange={(e) => handleItemChange(index, e)} 
                    className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                  />
                </div>
                <div className="md:col-span-2 space-y-1">
                  <Label htmlFor={`item-aantal-${index}`} className="md:hidden text-xs text-gray-600">Aantal</Label>
                  <Input 
                    id={`item-aantal-${index}`}
                    name="aantal" 
                    type="number" 
                    placeholder="Aantal" 
                    value={item.aantal || 0} 
                    onChange={(e) => handleItemChange(index, e)} 
                    min="0"
                    className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10" 
                  />
                </div>
                <div className="md:col-span-3 space-y-1">
                  <Label htmlFor={`item-prijs-${index}`} className="md:hidden text-xs text-gray-600">Prijs/Stuk</Label>
                  <Input 
                    id={`item-prijs-${index}`}
                    name="prijsPerStuk" 
                    type="number" 
                    step="0.01" 
                    placeholder="Prijs/st" 
                    value={item.prijsPerStuk || 0} 
                    onChange={(e) => handleItemChange(index, e)} 
                    min="0"
                    className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
                  />
                </div>
                <div className="md:col-span-1 flex items-center justify-end">
                  <Button 
                    type="button" 
                    variant="destructive" 
                    size="icon" 
                    onClick={() => removeItem(index)} 
                    className="h-8 w-8 rounded-full"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}

            <Button 
              type="button" 
              onClick={addItem} 
              variant="outline"
              className="border-[#1E4FCF] text-[#1E4FCF] hover:bg-[#1E4FCF]/5"
            >
              <PlusCircle className="mr-2 h-4 w-4" /> Item Toevoegen
            </Button>
            
            <div className="mt-4 p-4 border rounded-md bg-gray-50 flex flex-wrap justify-end">
              <div className="w-full md:w-1/3 space-y-2 divide-y divide-gray-200">
                <div className="flex justify-between items-center pb-2">
                  <span className="text-sm text-gray-600">Subtotaal (Excl. BTW):</span> 
                  <span className="font-medium">{(offerteData.totaalExclBTW || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' })}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-gray-600">BTW ({BTW_PERCENTAGE_VAST}%):</span> 
                  <span className="font-medium">{(offerteData.btwBedrag || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' })}</span>
                </div>
                <div className="flex justify-between items-center pt-2">
                  <span className="font-bold">Totaal (Incl. BTW):</span> 
                  <span className="font-bold text-[#1E4FCF]">{(offerteData.totaalInclBTW || 0).toLocaleString('nl-BE', { style: 'currency', currency: 'EUR' })}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-200 shadow-sm rounded-lg">
          <CardHeader className="pb-2 border-b border-gray-100">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileText className="h-4 w-4 text-[#1E4FCF]" /> Extra Informatie
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-2">
              <Label htmlFor="extraTekstOfferte" className="text-sm font-medium">Extra tekst op offerte</Label>
              <Textarea 
                id="extraTekstOfferte" 
                name="extraTekstOfferte" 
                value={offerteData.extraTekstOfferte} 
                onChange={handleChange}
                placeholder="Bijv. betalingsvoorwaarden, levertijd, garantie, etc."
                className="min-h-[100px] border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pt-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => navigate('/dashboard/offertes')}
            className="border-gray-300 text-gray-700 hover:bg-gray-50/80 rounded-md"
            disabled={loading}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Annuleren
          </Button>
          <Button 
            type="submit"
            className="bg-[#BFFF00] hover:bg-[#BFFF00]/90 text-[#05081A] font-medium rounded-md"
            disabled={loading || !!successMessage}
          >
            <Save className="mr-2 h-4 w-4" /> {loading ? 'Opslaan...' : 'Offerte Opslaan'}
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default OfferteForm;